import React from "react";
import { Eye, MessageSquare } from "lucide-react";
import { Button } from "../UI";
import { displayPersonName } from "../../utils/recruiterUtils";

export default function ShortlistedCandidatesModal({
  isOpen,
  shortlistItems = [],
  hiredCandidates = [],
  shortlistModalFilter = "all",
  setShortlistModalFilter,
  whatsAppMsgStatus = "",
  theme,
  fonts,
  onClose,
  onViewCandidateDetails,
  onOpenFeedbackModal,
  onSendWhatsApp
}) {
  if (!isOpen) return null;

  const filteredShortlists = shortlistItems.filter(rc => {
    if (shortlistModalFilter === "hired") return (rc.status || "").toLowerCase() === "hired";
    if (shortlistModalFilter === "rejected") return (rc.status || "").toLowerCase() === "rejected";
    if (shortlistModalFilter === "shortlisted") return !rc.status || (rc.status || "").toLowerCase() === "shortlisted";
    return true;
  });

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 1000
    }}>
      <div style={{ background: theme.surface, border: `1px solid ${theme.line}`, borderRadius: 14, padding: 28, width: "100%", maxWidth: 700, maxHeight: "85vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0, fontFamily: fonts?.display }}>Shortlisted Candidates & Hires ({shortlistItems.length})</h3>
            <p style={{ fontSize: 12, color: theme.textMute, margin: "2px 0 0" }}>Manage interview schedules, record feedback & track hired candidates</p>
          </div>
        </div>

        {/* Tabs / Filter bar */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          {[
            { id: "all", label: `All (${shortlistItems.length})` },
            { id: "shortlisted", label: `Shortlisted (${shortlistItems.filter(c => !c.status || c.status === 'shortlisted').length})` },
            { id: "hired", label: `Hired (${hiredCandidates.length})` },
            { id: "rejected", label: `Rejected (${shortlistItems.filter(c => c.status === 'rejected').length})` },
          ].map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setShortlistModalFilter(tab.id)}
              style={{
                padding: "6px 14px",
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                border: `1px solid ${shortlistModalFilter === tab.id ? (tab.id === 'hired' ? '#22c55e' : (tab.id === 'rejected' ? '#ef4444' : theme.accent)) : theme.line}`,
                background: shortlistModalFilter === tab.id ? (tab.id === 'hired' ? 'rgba(34,197,94,0.15)' : (tab.id === 'rejected' ? 'rgba(239,68,68,0.15)' : theme.accentDim)) : "transparent",
                color: shortlistModalFilter === tab.id ? (tab.id === 'hired' ? '#22c55e' : (tab.id === 'rejected' ? '#ef4444' : theme.accent)) : theme.textMute
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {whatsAppMsgStatus && (
          <div style={{ background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.4)", color: "#16a34a", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, marginBottom: 14 }}>
            OK {whatsAppMsgStatus}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filteredShortlists.length === 0 ? (
            <div style={{ border: `1px dashed ${theme.line}`, borderRadius: 8, padding: 24, textAlign: "center", color: theme.textMute, fontSize: 13 }}>
              {shortlistModalFilter === "hired" ? "No candidates hired yet. Record interview feedback on shortlisted candidates to mark them as hired." : "No candidates found in this view."}
            </div>
          ) : (
            filteredShortlists.map(rc => (
              <div key={rc.link_id || rc.id || rc.name} style={{ border: `1px solid ${theme.line}`, borderRadius: 10, padding: 16, background: theme.bg }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <h4 style={{ fontWeight: 700, fontSize: 15, margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
                      {displayPersonName(rc) || rc.candidate_name || rc.name || "Candidate"}
                      {rc.candidate_uan && <span style={{ fontSize: 10, color: theme.textMute }}>UAN: {rc.candidate_uan}</span>}
                    </h4>
                    <div style={{ fontSize: 12, color: theme.accent, marginTop: 2, fontWeight: 600 }}>
                      Role: {rc.job_role || rc.role || "Software Position"}
                    </div>
                    <div style={{ fontSize: 11.5, color: theme.textMute, marginTop: 4 }}>
                      <strong>Interview Date:</strong> {rc.interview_date || "Not scheduled"} | <strong>AI Match:</strong> {rc.match_score != null ? `${rc.match_score}%` : " - "}
                    </div>
                  </div>

                  <span style={{
                    fontSize: 11, fontWeight: 700, borderRadius: 6, padding: "3px 10px",
                    background: rc.status === "hired" ? "rgba(34,197,94,0.15)" : (rc.status === "rejected" ? "rgba(239,68,68,0.15)" : "rgba(255,210,76,0.15)"),
                    color: rc.status === "hired" ? "#22c55e" : (rc.status === "rejected" ? "#ef4444" : theme.accent)
                  }}>
                    {(rc.status || "SHORTLISTED").toUpperCase()}
                  </span>
                </div>

                {rc.feedback && (
                  <div style={{ background: theme.surface, borderRadius: 6, padding: 10, marginTop: 10, fontSize: 12 }}>
                    <div><strong>Recruiter Feedback:</strong> "{rc.feedback}"</div>
                    {rc.rating && <div><strong>Rating:</strong> {rc.rating}/10</div>}
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                  {rc.employee_id && (
                    <Button theme={theme} onClick={() => {
                      onViewCandidateDetails({ id: rc.employee_id, employeeId: rc.employee_id, name: rc.candidate_name, title: rc.job_role, uan: rc.candidate_uan });
                      onClose();
                    }} style={{ padding: "5px 12px", fontSize: 11 }}>
                      <Eye size={12} /> View Candidate Profile
                    </Button>
                  )}
                  <Button
                    theme={{ ...theme, surface: theme.line }}
                    onClick={() => {
                      onOpenFeedbackModal(rc);
                      onClose();
                    }}
                    style={{
                      padding: "5px 12px",
                      fontSize: 11,
                      background: rc.status === "hired" ? "rgba(34,197,94,0.1)" : "transparent",
                      border: `1px solid ${rc.status === "hired" ? "#22c55e" : theme.line}`,
                      color: rc.status === "hired" ? "#16a34a" : theme.text
                    }}
                  >
                    {rc.status === "hired" ? "Update Feedback / Rating" : (rc.status === "rejected" ? "Update Decision" : "Record Interview Feedback")}
                  </Button>
                  <Button theme={{ ...theme, surface: theme.line }} onClick={() => onSendWhatsApp(rc)} style={{ padding: "5px 12px", fontSize: 11, background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)", color: "#16a34a" }}>
                    <MessageSquare size={12} /> Send WhatsApp Alert
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        <div style={{ marginTop: 20, textAlign: "right" }}>
          <Button theme={{ ...theme, surface: theme.line }} onClick={onClose} style={{ background: "transparent", border: `1px solid ${theme.line}`, color: theme.text }}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
